import { Crown } from 'lucide-react';
import type { CmsBlock } from './BlockRenderer';

// ─── Types & mock data ────────────────────────────────────────────────────────

interface LeaderboardEntry {
  pos: number;
  name: string;
  points: number;
  level: number;
  isCurrentUser?: boolean;
}

const LEADERBOARD: LeaderboardEntry[] = [
  { pos: 1, name: 'Alessandro R.', points: 24500, level: 15 },
  { pos: 2, name: 'Giulia M.', points: 18200, level: 13 },
  { pos: 3, name: 'Marco Rossi', points: 12450, level: 12, isCurrentUser: true },
  { pos: 4, name: 'Lorenzo P.', points: 11200, level: 11 },
  { pos: 5, name: 'Sara V.', points: 9500, level: 9 },
  { pos: 6, name: 'Davide C.', points: 8900, level: 9 },
  { pos: 7, name: 'Chiara F.', points: 8100, level: 8 },
];

// ─── Avatar with initials ─────────────────────────────────────────────────────

function Avatar({
  name,
  size = 'md',
  ring,
}: {
  name: string;
  size?: 'sm' | 'md' | 'lg';
  ring?: string;
}) {
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const sizeClass = { sm: 'w-10 h-10 text-sm', md: 'w-14 h-14 text-base', lg: 'w-20 h-20 text-xl' }[size];
  const ringStyle = ring ? { boxShadow: `0 0 0 3px ${ring}` } : {};

  return (
    <div
      className={`${sizeClass} rounded-full bg-[#1d293d] flex items-center justify-center shrink-0 font-bold text-[#90a1b9]`}
      style={ringStyle}
    >
      {initials}
    </div>
  );
}

// ─── Podium component ─────────────────────────────────────────────────────────

const PODIUM_CONFIG = [
  {
    pos: 2,
    heightClass: 'h-[160px]',
    barBg: 'bg-[#1d293d]',
    numColor: 'text-[#90a1b9]',
    ringColor: undefined,
    avatarSize: 'md' as const,
  },
  {
    pos: 1,
    heightClass: 'h-[220px]',
    barBg: 'bg-gradient-to-t from-[#b8860b] to-[#f0b100]',
    numColor: 'text-[#f0b100]',
    ringColor: '#f0b100',
    avatarSize: 'lg' as const,
    hasCrown: true,
  },
  {
    pos: 3,
    heightClass: 'h-[130px]',
    barBg: 'bg-gradient-to-t from-[#7c5c3e] to-[#cd7f32]',
    numColor: 'text-[#cd7f32]',
    ringColor: '#cd7f32',
    avatarSize: 'md' as const,
  },
];

function PodiumColumn({ entry, config }: { entry: LeaderboardEntry; config: typeof PODIUM_CONFIG[number] }) {
  return (
    <div className="flex flex-col items-center gap-3 flex-1">
      {/* Crown for 1st */}
      {config.hasCrown && (
        <Crown size={28} className="text-[#f0b100]" />
      )}
      {!config.hasCrown && <div className="h-7" />}

      {/* Avatar */}
      <Avatar name={entry.name} size={config.avatarSize} ring={config.ringColor} />

      {/* Name & points */}
      <div className="text-center">
        <p className="text-white font-black text-lg leading-6 truncate max-w-[100px]">
          {entry.name.split(' ')[0]}
        </p>
        <p className="text-[#90a1b9] text-sm font-bold">
          {entry.points.toLocaleString('it-IT')} pts
        </p>
      </div>

      {/* Podium bar */}
      <div
        className={`w-full ${config.heightClass} ${config.barBg} rounded-t-2xl flex items-start justify-center pt-4`}
      >
        <span className={`font-black text-5xl opacity-40 ${config.numColor}`}>{config.pos}</span>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function LoyaltyLeaderboardBlock(_: { block?: CmsBlock }) {
  const podiumOrder = [2, 1, 3]; // display order: 2nd, 1st, 3rd
  const listEntries = LEADERBOARD.slice(3); // pos 4+

  return (
    <div className="p-10 flex flex-col gap-8">
      {/* ── Page heading ─────────────────────────────────────────────────── */}
      <div>
        <h1 className="text-white font-black text-[36px] leading-10 tracking-tight">
          Classifica Globale
        </h1>
        <p className="text-[#90a1b9] text-base mt-1">
          I migliori giocatori del Premium Club. Scala la classifica mensile per aggiudicarti
          ricompense esclusive, bonus e biglietti per eventi sportivi reali.
        </p>
      </div>

      {/* ── Main layout: Podio + Lista ────────────────────────────────────── */}
      <div className="flex gap-6 items-end">
        {/* Podio */}
        <div className="flex-1 flex items-end gap-4 min-h-[340px]">
          {podiumOrder.map((pos) => {
            const entry = LEADERBOARD.find((e) => e.pos === pos);
            const config = PODIUM_CONFIG.find((c) => c.pos === pos);
            if (!entry || !config) return null;
            return <PodiumColumn key={pos} entry={entry} config={config} />;
          })}
        </div>

        {/* Lista posizioni 4-100 */}
        <div className="w-[340px] bg-[#0f172b] border border-white/5 rounded-3xl overflow-hidden shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] self-stretch">
          {/* Header */}
          <div className="flex items-center gap-2 px-6 py-5 border-b border-white/5">
            <Crown size={18} className="text-[#90a1b9]" />
            <p className="text-white font-bold text-base">Posizioni 4 - 100</p>
          </div>

          {/* Rows */}
          <div className="flex flex-col divide-y divide-white/[0.04]">
            {listEntries.map((entry) => (
              <div
                key={entry.pos}
                className={`flex items-center gap-4 px-6 py-4 transition-colors hover:bg-white/[0.02] ${
                  entry.isCurrentUser ? 'bg-[rgba(0,188,125,0.05)]' : ''
                }`}
              >
                {/* Position number */}
                <span className="w-5 text-[#62748e] font-bold text-sm text-right shrink-0">
                  {entry.pos}
                </span>

                {/* Avatar */}
                <Avatar name={entry.name} size="sm" ring={entry.isCurrentUser ? '#00bc7d' : undefined} />

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p
                    className={`font-bold text-sm truncate ${
                      entry.isCurrentUser ? 'text-[#00d492]' : 'text-[#e2e8f0]'
                    }`}
                  >
                    {entry.name}
                  </p>
                  <p className="text-[#62748e] text-xs">Livello {entry.level}</p>
                </div>

                {/* Points */}
                <span className="text-[#00d492] font-black text-sm whitespace-nowrap shrink-0">
                  {entry.points.toLocaleString('it-IT')}
                  <span className="text-[#62748e] font-medium text-xs ml-1">PUNTI</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
